import { useDispatch, useSelector } from 'react-redux';
import Breadcrumbs from './BreadCrumbs';
import {
  useProductName,
  contactInfoRequest,
  renderDescriptionParagraphs,
} from '@rb-serve-app/core';
import { useEffect } from 'react';
import Spinner from './Spinner';
import { useLocation, useNavigate } from 'react-router-dom';
import { logBrazeCustomEvent } from '../config/braze';

const RequestSupplementaryCard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );

  const { accountId, customerId } = useSelector((state: any) => state.user);
  const { contactDetails, loading } = useSelector(
    (state: any) => state.contactInfo
  );
  const { productName } = useProductName();
  const { entries, assets } = contentfulData;
  const { rb_supplementaryCards } = entries;

  const location = useLocation();
  const shouldRefresh = location.state?.refresh;

  useEffect(() => {
    if (accountId && customerId) {
      if (shouldRefresh) {
        dispatch(
          contactInfoRequest({
            accountId: accountId,
            primaryCustomerId: customerId,
            customerId: customerId,
          })
        );
      }
    }
  }, [shouldRefresh]);

  useEffect(() => {
    if (accountId && customerId) {
      if (contactDetails === null && !loading) {
        dispatch(
          contactInfoRequest({
            accountId: accountId,
            primaryCustomerId: customerId,
            customerId: customerId,
          })
        );
      }
    }
  }, [accountId, customerId]);

  useEffect(() => {
    logBrazeCustomEvent('viewed_add_new_cardholder');
  }, []);

  return (
    <div className="bg-[#fafafa] flex flex-col py-4 px-8">
      <Breadcrumbs
        items={[
          {
            name: entries?.rb_manage_card?.manageCardTitle,
            href: '/manage-card',
          },
          {
            name: rb_supplementaryCards?.addSupplementaryCardDetails
              ?.bread_crumbs?.title1,
            href: '/request-supplementary',
          },
        ]}
      />
      <div className="bg-white min-h-[70vh] px-[27px] py-[29px] rounded-[15px]">
        {!loading ? (
          <>
            <div className="">
              <h1
                aria-label={
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.view_available_cards?.title
                }
                className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] pb-1"
              >
                {renderDescriptionParagraphs(
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.view_available_cards?.title,
                  ' '
                )}
              </h1>
              <p
                aria-label={`${
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.view_available_cards?.title
                } ${productName} ${contactDetails?.availableCards[0]?.cardNumber?.slice(-4)}`}
              >
                {renderDescriptionParagraphs(
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.view_available_cards?.description1 +
                    ' ' +
                    productName +
                    '..' +
                    contactDetails?.availableCards[0]?.cardNumber?.slice(-4) +
                    ' ' +
                    rb_supplementaryCards?.addSupplementaryCardDetails
                      ?.view_available_cards?.description2,
                  ' '
                )}
                {contactDetails?.availableCards?.length > 1 && (
                  <a
                    aria-label={
                      rb_supplementaryCards?.addSupplementaryCardDetails
                        ?.view_available_cards?.description3
                    }
                    className="cursor-pointer text-primary-red"
                  >
                    {
                      rb_supplementaryCards?.addSupplementaryCardDetails
                        ?.view_available_cards?.description3
                    }
                  </a>
                )}
              </p>
            </div>
            {contactDetails?.availableCards[0]?.cardNumber ? (
              <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mt-10">
                <div
                  className={`col-span-1 ${contactDetails?.availableCards?.length > 9 ? 'hidden' : ''}`}
                >
                  {contactDetails?.availableCards?.length <= 9 && (
                    <button
                      className="w-full h-20 bg-gray-100 text-primary-red rounded-md border-primary-red border-2"
                      onClick={() => navigate('/supplementary-details')}
                    >
                      <div className="flex flex-row">
                        <img
                          src={assets?.add_card_icon}
                          alt="addIcon"
                          className="inline w-[50px] h-[50px] mx-6"
                        />
                        <p
                          className="mt-3"
                          aria-label={
                            rb_supplementaryCards?.addSupplementaryCardDetails
                              ?.view_available_cards?.add_cardHolder_cta
                          }
                        >
                          {
                            rb_supplementaryCards?.addSupplementaryCardDetails
                              ?.view_available_cards?.add_cardHolder_cta
                          }
                        </p>
                        <img
                          src={assets?.right_arrow}
                          alt="rightIcon"
                          className="inline w-[30px] h-[30px] mt-3 ml-12"
                        />
                      </div>
                    </button>
                  )}
                </div>

                {contactDetails?.availableCards
                  ?.slice(1)
                  .map((card: any, index: any) => (
                    <div
                      key={index}
                      className="flex flex-row h-20 border rounded-md bg-gray-100"
                      role="listitem"
                      aria-labelledby={`card-${index}`}
                    >
                      <img
                        src={assets?.badge_icon}
                        alt="badgeIcon"
                        className="inline w-[50px] h-[50px] mx-6 mt-3"
                      />
                      <div className="mt-3" id={`card-${index}`}>
                        <div
                          className="font-semibold"
                          aria-label={card?.nameOnCard?.slice(0, 20)}
                        >
                          {card?.nameOnCard?.slice(0, 20)}
                        </div>
                        <div
                          className="text-black-600"
                          aria-label={`${
                            rb_supplementaryCards?.addSupplementaryCardDetails
                              ?.view_available_cards?.card_number
                          } ${card?.cardNumber.slice(-4)}`}
                        >
                          {
                            rb_supplementaryCards?.addSupplementaryCardDetails
                              ?.view_available_cards?.card_number
                          }{' '}
                          {card?.cardNumber.slice(-4)}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="flex justify-center items-center h-[70vh]">
                <Spinner />
              </div>
            )}
          </>
        ) : (
          <div className="flex justify-center items-center h-[70vh]">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestSupplementaryCard;
